import React, { useCallback, useContext, useEffect, useState } from "react";
import { DrawModeChangeEvent } from "@mapbox/mapbox-gl-draw";
import { Box, Divider, ToggleButtonGroup } from "@mui/material";

import { MapContext } from "../MapProvider";

import { ToolbarGroup } from "./tools";

import { Paper } from "./toolbar.styles";

export interface IButtonProps {
  title?: string;
  subtitle?: string;
  tooltip?: string;
  icon?: React.ReactNode;
  mode?: string;
  variant?: "minimal" | "full";
}

const Toolbar: React.FC = () => {
  const { map, drawControl, isDrawing, toolbar } = useContext(MapContext);
  const [mode, setMode] = useState<string | null>(null);

  useEffect(() => {
    if (isDrawing && drawControl.current && map?.hasControl(drawControl.current)) {
      setMode(drawControl.current.getMode());
    }
  }, [isDrawing, drawControl, map]);

  /**
   * Handles the changing of the current drawmode
   */
  const handleModeChange = useCallback(
    (event: DrawModeChangeEvent) => {
      setMode(event.mode);
    },
    [setMode]
  );

  useEffect(() => {
    map?.on("draw.modechange", handleModeChange);
    return () => {
      map?.off("draw.modechange", handleModeChange);
    };
  }, [map, handleModeChange]);

  if (!isDrawing) return null;

  const renderButtonGroup = (group: ToolbarGroup) => {
    const { items, id } = group;
    return (
      <Box key={id}>
        <ToggleButtonGroup exclusive orientation="vertical" size="small" value={mode}>
          {items.map((item) => React.cloneElement(item, { mode }))}
        </ToggleButtonGroup>
      </Box>
    );
  };

  const renderActionGroup = (group: ToolbarGroup) => {
    const { items, id } = group;
    return (
      <Box key={id} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {items.map((item) => React.cloneElement(item, { mode }))}
      </Box>
    );
  };

  return (
    <Paper elevation={3} sx={{ position: "absolute", top: 5, left: 5, display: "flex", flexDirection: "column", zIndex: 999 }}>
      {toolbar.current?.map((config, index) => (
        <React.Fragment key={config.id}>
          {config.type === "mode" && renderButtonGroup(config)}
          {config.type === "action" && renderActionGroup(config)}
          {index < (toolbar.current?.length || 0) - 1 && <Divider sx={{ my: 0.5 }} />}
        </React.Fragment>
      ))}
    </Paper>
  );
};

export default Toolbar;
